import { useState } from 'react';
import { DocumentScannerOutlined } from '@mui/icons-material';
import { Autocomplete, Box, FormControlLabel, Link, Switch, TextField, Typography } from '@mui/material';
import { useCreate, useGo } from '@refinedev/core';
import { Create } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { format } from 'date-fns';
import { FieldValues, FormProvider, SubmitHandler, useController, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { Dropzone, TextFieldInput } from '@attackiq/components';
import { ATTACKIQ_ID } from '@attackiq/constants';
import { fileToBase64 } from '@attackiq/utils';

import { ShowInAppFormGroup } from '../../components';
import { ThreatInformedDefense } from '../../types';

export const HighlightInput = () => {
  const { control } = useFormContext();
  const { field } = useController({
    name: 'highlight',
    control
  });

  const options = [
    { model: false, displayName: 'No' },
    { model: true, displayName: 'Yes' }
  ];

  return (
    <Autocomplete
      renderInput={params => <TextField {...params} label="Highlight" />}
      options={options}
      onChange={(_e, value) => {
        field.onChange(value?.model);
      }}
      value={options.find(option => option.model === field.value)}
      getOptionLabel={option => option.displayName}
    />
  );
};

export const ActiveInput = () => {
  const { control } = useFormContext();
  const { field } = useController({
    name: 'active',
    control
  });

  const options = [
    { model: false, displayName: 'No' },
    { model: true, displayName: 'Yes' }
  ];

  return (
    <Autocomplete
      renderInput={params => <TextField {...params} label="Active" />}
      options={options}
      onChange={(_e, value) => {
        field.onChange(value?.model);
      }}
      value={options.find(option => option.model === field.value)}
      getOptionLabel={option => option.displayName}
    />
  );
};

const ThreatInformedDefenseCreate = () => {
  const {
    mutateAsync: createMutateAsync,
    mutation: { isPending }
  } = useCreate();

  const go = useGo();

  const [selectedFile, setSelectedFile] = useState<File | undefined | null>(null);
  const [urlEnabled, setUrlEnabled] = useState(false);

  const methods = useForm<ThreatInformedDefense>({
    refineCoreProps: { action: 'create' },
    defaultValues: {
      title: '',
      description: '',
      url: '',
      date: '',
      cover_image: null,
      highlight: false,
      active: false,
      company: ATTACKIQ_ID,
      id: '',
      show_in_enterprise: false,
      show_in_ready: false,
      show_in_flex: false
    }
  });

  const { handleSubmit, setValue } = methods;

  const onSubmit: SubmitHandler<ThreatInformedDefense> = async tidContent => {
    const payload = {
      ...tidContent,
      id: uuidv4(),
      date: format(new Date(), 'yyyy-MM-dd')
    };

    if (!urlEnabled) {
      payload.cover_image = selectedFile ? await fileToBase64(selectedFile) : '';
    }

    const { data: createdTid } = await createMutateAsync({
      resource: 'threat_informed_defense',
      values: payload
    });

    if (!createdTid.id) return;

    go({
      to: {
        resource: 'threat_informed_defense',
        action: 'edit',
        id: createdTid.id
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        name="create-module-form"
        onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
        autoComplete="off"
      >
        <Create isLoading={isPending} saveButtonProps={{ type: 'submit' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <ActiveInput />
            <TextFieldInput color="primary" name="title" label="Title" rules={{ required: 'Title is required' }} />
            <TextFieldInput
              color="primary"
              multiline
              rows={5}
              name="description"
              label="Description"
              rules={{ required: 'Description is required' }}
            />
            <TextFieldInput color="primary" name="url" label="URL" />
            <HighlightInput />
            <FormControlLabel
              label="Use URL to upload cover image"
              control={
                <Switch
                  value={urlEnabled}
                  onChange={e => {
                    setValue('cover_image', '');
                    setUrlEnabled(e.target.checked);
                  }}
                />
              }
            />
            {urlEnabled ? (
              <TextFieldInput
                color="primary"
                name="cover_image"
                label="Cover Image URL"
                rules={{
                  required: 'Cover Image is required',
                  pattern: { value: /https?:\/\/.*/, message: 'Invalid URL' }
                }}
              />
            ) : (
              <Dropzone
                rules={{
                  accept: '.png, .jpg, .jpeg, .svg, .webp'
                }}
                onDropAccepted={files => {
                  setSelectedFile(files[0]);
                }}
                width="100%"
                height={152}
              >
                <Dropzone.Body>
                  <>
                    <Typography>
                      <Link component="span">Click to upload</Link> or drag and drop
                    </Typography>
                    <Typography color="text.secondary">
                      .png, .jpg, .jpeg, .svg, .webp files supported only (max. 3MB)
                    </Typography>
                  </>
                </Dropzone.Body>

                <Dropzone.Cover
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  bgcolor="background.paper"
                  in={!!selectedFile}
                >
                  <DocumentScannerOutlined color="primary" />
                  <Typography>{selectedFile?.name}</Typography>
                </Dropzone.Cover>
              </Dropzone>
            )}
            <ShowInAppFormGroup />
          </Box>
        </Create>
      </form>
    </FormProvider>
  );
};

export default ThreatInformedDefenseCreate;
